/*
=========================================================
* Material Kit 2 React - v2.1.0
=========================================================
*/
import { useMemo } from "react";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

// Material Kit 2 React examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

// Shared sections (already customized for this project)
import ContactUsSection from "pages/LandingPages/AboutUs/sections/contactUs";
import Newsletter from "pages/LandingPages/AboutUs/sections/Newsletter";

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

// Images
import heroImage from "assets/images/production/construction-site.jpg";
import safetyImage from "assets/images/site_safety.jpg";
import screedingImage from "assets/images/screeding.png";
import cuttingImage from "assets/images/Image_showing_concrete_diamond_cutting.jpeg";
import cleaningImage from "assets/images/site_cleaning.jpg";

function AboutOurCompany() {
  const highlights = useMemo(
    () => [
      {
        title: "Craft-led delivery",
        description:
          "We bring specialist crews, modern equipment, and clear site coordination to keep work moving and standards high.",
        icon: "handyman",
      },
      {
        title: "Safety-first thinking",
        description:
          "Method statements, risk controls, and tidy working areas are built into how we operate—every day, every job.",
        icon: "health_and_safety",
      },
      {
        title: "Predictable timelines",
        description:
          "We plan around access, sequencing, and handover requirements so you get dependable progress and clean finishes.",
        icon: "schedule",
      },
    ],
    []
  );

  const signatureWork = useMemo(
    () => [
      {
        title: "Concrete screeding",
        description:
          "High-precision screeds for level, durable substrates—ready for flooring systems and heavy-use environments.",
        image: screedingImage,
      },
      {
        title: "Concrete diamond cutting & drilling",
        description:
          "Clean, accurate cutting and core drilling for openings, structural modifications, and service routes with controlled dust and noise.",
        image: cuttingImage,
      },
      {
        title: "Project handover cleaning",
        description:
          "Presentation-ready sites with detailed cleaning that supports snagging, client walkthroughs, and safe handover.",
        image: cleaningImage,
      },
    ],
    []
  );

  return (
    <>
      <DefaultNavbar
        routes={routes}
        action={{
          type: "internal",
          route: "/pages/landing-pages/contact-us",
          label: "Contact Us",
          color: "default",
        }}
        transparent
        light
      />

      {/* Header / hero */}
      <MKBox
        minHeight="75vh"
        width="100%"
        position="relative"
        overflow="hidden"
        display="grid"
        placeItems="center"
      >
        <MKBox
          aria-hidden
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          sx={({ functions: { linearGradient, rgba }, palette: { gradients } }) => ({
            backgroundImage: `${linearGradient(
              rgba(gradients.dark.main, 0.7),
              rgba(gradients.dark.state, 0.7)
            )}, url(${heroImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          })}
        />
        <Container sx={{ height: "80%", display: "flex", alignItems: "center" }}>
          <Grid
            container
            item
            xs={12}
            lg={8}
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{ mx: "auto", textAlign: "center", position: "relative", zIndex: 1 }}
          >
            <MKTypography
              variant="h1"
              color="white"
              sx={({ breakpoints, typography: { size } }) => ({
                [breakpoints.down("md")]: {
                  fontSize: size["3xl"],
                },
              })}
            >
              About our company
            </MKTypography>
            <MKTypography variant="body1" color="white" opacity={0.85} mt={1} mb={3}>
              We’re a UK-based construction team delivering screeding, concrete cutting, and
              handover-ready cleaning—built around safety, precision, and dependable timelines.
            </MKTypography>
            <MKBox display="flex" gap={1} flexWrap="wrap" justifyContent="center">
              <MKButton
                color="default"
                sx={{ color: ({ palette: { dark } }) => dark.main }}
                component="a"
                href="#contact-us"
              >
                Request a service
              </MKButton>
              <MKButton variant="outlined" color="white" component="a" href="#how-we-work">
                How we work
              </MKButton>
            </MKBox>
          </Grid>
        </Container>
      </MKBox>

      {/* Main content */}
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 0,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        {/* Intro */}
        <MKBox component="section" py={6}>
          <Container>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <MKTypography variant="h3" mb={1}>
                  Built for modern sites
                </MKTypography>
                <MKTypography variant="body1" color="text" mb={2}>
                  Our work sits in the critical path of delivery: level floors, clean openings, and
                  safe, presentable handovers. We help main contractors, developers, and facilities
                  teams keep projects moving without surprises.
                </MKTypography>
                <MKTypography variant="body2" color="text">
                  From planning through completion, we focus on clear communication, site safety,
                  and workmanship that holds up under inspection.
                </MKTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <MKBox
                  component="img"
                  src={safetyImage}
                  alt="On-site screeding work"
                  width="100%"
                  sx={({ borders: { borderRadius }, boxShadows: { md } }) => ({
                    borderRadius: borderRadius.lg,
                    boxShadow: md,
                    objectFit: "cover",
                    maxHeight: 380,
                  })}
                />
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        {/* Highlights */}
        <MKBox component="section" py={6} bgColor="grey-100">
          <Container>
            <Grid container item xs={12} md={8} sx={{ mx: "auto", textAlign: "left", mb: 4 }}>
              <MKTypography variant="h3" mb={1}>
                What we’re known for
              </MKTypography>
              <MKTypography variant="body1" color="text">
                The fundamentals that clients come back for—project after project.
              </MKTypography>
            </Grid>
            <Grid container spacing={3}>
              {highlights.map(({ title, description, icon }) => (
                <Grid key={title} item xs={12} md={4}>
                  <MKBox
                    height="100%"
                    borderRadius="lg"
                    p={3}
                    sx={({ palette: { background }, boxShadows: { md } }) => ({
                      backgroundColor: background.paper,
                      boxShadow: md,
                    })}
                  >
                    <MKBox
                      display="inline-flex"
                      alignItems="center"
                      justifyContent="center"
                      width="3rem"
                      height="3rem"
                      borderRadius="lg"
                      variant="gradient"
                      bgColor="info"
                      coloredShadow="info"
                      mb={2}
                    >
                      <Icon sx={{ color: "white" }}>{icon}</Icon>
                    </MKBox>
                    <MKTypography variant="h5" mb={1}>
                      {title}
                    </MKTypography>
                    <MKTypography variant="body2" color="text">
                      {description}
                    </MKTypography>
                  </MKBox>
                </Grid>
              ))}
            </Grid>
          </Container>
        </MKBox>

        {/* How we work */}
        <MKBox id="how-we-work" component="section" py={6} style={{ height: "50vh" }}>
          <Container>
            <Grid
              container
              item
              xs={12}
              md={8}
              sx={{ mx: "auto", textAlign: "left", mb: 4, flexDirection: "column" }}
            >
              <MKTypography variant="h3" mb={1}>
                How we work
              </MKTypography>
              <MKTypography variant="body1" color="text">
                A simple process that keeps sites safe, tidy, and on schedule.
              </MKTypography>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <MKBox
                  p={3}
                  borderRadius="lg"
                  sx={({ palette: { background } }) => ({ backgroundColor: background.paper })}
                >
                  <MKTypography variant="h6" mb={1}>
                    01. Scope & site plan
                  </MKTypography>
                  <MKTypography variant="body2" color="text">
                    We confirm access, sequencing, and constraints so the job fits the programme.
                  </MKTypography>
                </MKBox>
              </Grid>
              <Grid item xs={12} md={4}>
                <MKBox
                  p={3}
                  borderRadius="lg"
                  sx={({ palette: { background } }) => ({ backgroundColor: background.paper })}
                >
                  <MKTypography variant="h6" mb={1}>
                    02. Execution & quality checks
                  </MKTypography>
                  <MKTypography variant="body2" color="text">
                    Specialist crews deliver with the right equipment, controls, and on-site checks.
                  </MKTypography>
                </MKBox>
              </Grid>
              <Grid item xs={12} md={4}>
                <MKBox
                  p={3}
                  borderRadius="lg"
                  sx={({ palette: { background } }) => ({ backgroundColor: background.paper })}
                >
                  <MKTypography variant="h6" mb={1}>
                    03. Handover-ready finish
                  </MKTypography>
                  <MKTypography variant="body2" color="text">
                    We leave areas clean and documented—ready for the next trade and client
                    sign-off.
                  </MKTypography>
                </MKBox>
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        {/* Signature work */}
        <MKBox
          component="section"
          py={6}
          sx={{
            p: 2,
            mx: { xs: 2, lg: 3 },
            mt: 30,
          }}
        >
          <Container>
            <Grid container item xs={12} md={8} sx={{ mx: "auto", textAlign: "left", mb: 4 }}>
              <MKTypography variant="h3" mb={1}>
                Our signature work
              </MKTypography>
              <MKTypography variant="body1" color="text">
                Three services we’re trusted with most—delivered to spec and ready for inspection.
              </MKTypography>
            </Grid>
            <Grid container spacing={3}>
              {signatureWork.map(({ title, description, image }) => (
                <Grid key={title} item xs={12} md={4}>
                  <MKBox
                    height="100%"
                    borderRadius="lg"
                    overflow="hidden"
                    sx={({ boxShadows: { md }, palette: { background } }) => ({
                      backgroundColor: background.paper,
                      boxShadow: md,
                    })}
                  >
                    <MKBox
                      component="img"
                      src={image}
                      alt={title}
                      width="100%"
                      height="220px"
                      sx={{ objectFit: "cover" }}
                    />
                    <MKBox p={3}>
                      <MKTypography variant="h5" mb={1}>
                        {title}
                      </MKTypography>
                      <MKTypography variant="body2" color="text">
                        {description}
                      </MKTypography>
                    </MKBox>
                  </MKBox>
                </Grid>
              ))}
            </Grid>
          </Container>
        </MKBox>
      </Card>

      {/* Contact */}
      <MKBox id="contact-us" />
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: 4,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <ContactUsSection />
      </Card>

      {/* Newsletter */}
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <Newsletter />
      </Card>

      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default AboutOurCompany;
